import {
  useMutation,
  useQuery,
  useQueryClient,
  type UseMutationOptions,
} from '@tanstack/react-query';
import { dataCentralApi } from '@/api/client';
import { billingKeys } from '@/api/billing';

/**
 * Payment-gateway data layer for paying an invoice. All endpoints are on the v4
 * DataCentral backend (`/partner/payments/*`). These move real money once the
 * user completes a flow — the UI wires them, but never auto-submits.
 */

// Rough VND→USD used only to show an approximate charge for card/PayPal.
const VND_TO_USD_RATE = 25_000;

export function convertVndToUsd(amountVnd: number): number {
  return Math.ceil(amountVnd / VND_TO_USD_RATE + 1);
}

export function generatePaymentReference(
  customerId: string | number | undefined,
  invoiceId: string | number | undefined,
  stamp: number,
): string {
  return `INV${invoiceId ?? '0'}-${customerId ?? '0'}-${stamp}`;
}

function result<T>(data: unknown): T {
  const d = data as { result?: T; data?: T };
  return (d?.result ?? d?.data) as T;
}

// ---- VietQR / bank transfer ----

export interface VietQRResult {
  qrCodeData: string;
  qrCodeBase64: string;
  bankInfo?: Record<string, unknown>;
  accountInfo?: Record<string, unknown>;
  amount: number;
  reference: string;
  purpose: string;
}

export interface VietQRRequest {
  invoiceId: string | number;
  amount: number;
  purpose?: string;
  reference?: string;
}

export function useGenerateVietQR(
  options?: UseMutationOptions<VietQRResult, unknown, VietQRRequest>,
) {
  return useMutation({
    mutationFn: async (req: VietQRRequest) => {
      const res = await dataCentralApi.post('/partner/payments/vietqr/generate', {
        invoiceId: String(req.invoiceId),
        amount: Number(req.amount),
        purpose: req.purpose || '',
        reference: req.reference || '',
      });
      return result<VietQRResult>(res.data);
    },
    ...options,
  });
}

// ---- MoMo ----

export interface MoMoResult {
  requestId: string;
  orderId: string;
  amount: number;
  payUrl: string;
  shortLink?: string;
  deeplink?: string;
  qrCodeUrl?: string;
  qrCodeBase64?: string;
  expiresIn?: number;
}

export interface MoMoRequest {
  orderId: string;
  amount: number;
  orderInfo?: string;
  invoiceId?: string;
  redirectUrl?: string;
}

export function useCreateMoMoPayment(
  options?: UseMutationOptions<MoMoResult, unknown, MoMoRequest>,
) {
  return useMutation({
    mutationFn: async (req: MoMoRequest) => {
      const res = await dataCentralApi.post('/partner/payments/momo/generate', {
        orderId: String(req.orderId),
        amount: Number(req.amount),
        orderInfo: req.orderInfo || '',
        requestType: 'captureWallet',
        invoiceId: req.invoiceId || '',
        redirectUrl: req.redirectUrl || '',
      });
      return result<MoMoResult>(res.data);
    },
    ...options,
  });
}

export interface MoMoStatus {
  orderId: string;
  amount: number;
  resultCode: number;
  message: string;
}

/** Poll MoMo transaction status. resultCode 0 = success, 1000 = pending. */
export function useMoMoStatus(orderId: string | null) {
  return useQuery<MoMoStatus | null>({
    queryKey: ['billing', 'momo-status', orderId],
    enabled: !!orderId,
    refetchInterval: (q) => {
      const code = (q.state.data as MoMoStatus | null)?.resultCode;
      // Keep polling while pending (1000); stop once resolved.
      return code === undefined || code === 1000 ? 4000 : false;
    },
    queryFn: async () => {
      const res = await dataCentralApi.get(`/partner/payments/momo/status/${orderId}`);
      return result<MoMoStatus>(res.data) ?? null;
    },
  });
}

// ---- PayPal ----

export interface PayPalResult {
  orderId: string;
  status: string;
  amount: number;
  amountVnd: number;
  currency: string;
  reference: string;
  approveUrl: string;
}

export interface PayPalRequest {
  amount: number;
  currency?: string;
  reference: string;
  description?: string;
  invoiceId?: string;
  returnUrl?: string;
  cancelUrl?: string;
}

export function useCreatePayPalOrder(
  options?: UseMutationOptions<PayPalResult, unknown, PayPalRequest>,
) {
  return useMutation({
    mutationFn: async (req: PayPalRequest) => {
      const res = await dataCentralApi.post('/partner/payments/paypal/orders', {
        amount: Number(req.amount),
        currency: req.currency || 'VND',
        reference: req.reference,
        description: req.description || '',
        invoiceId: req.invoiceId || '',
        returnUrl: req.returnUrl || '',
        cancelUrl: req.cancelUrl || '',
      });
      return result<PayPalResult>(res.data);
    },
    ...options,
  });
}

export interface PayPalCaptureResult {
  orderId: string;
  status: string;
  captureId: string;
  amount: number;
  currency: string;
  reference: string;
  payerEmail?: string;
}

/**
 * Capture an approved PayPal order. Called from the PayPal return route once the
 * payer approves — finalises the payment. Mirrors legacy capturePayPalOrder
 * (`/partner/payments/paypal/orders/capture`).
 */
export function useCapturePayPalOrder(
  options?: UseMutationOptions<PayPalCaptureResult, unknown, { orderId: string; reference?: string }>,
) {
  return useMutation({
    mutationFn: async (req: { orderId: string; reference?: string }) => {
      const res = await dataCentralApi.post('/partner/payments/paypal/orders/capture', {
        orderId: req.orderId,
        reference: req.reference || '',
      });
      return result<PayPalCaptureResult>(res.data);
    },
    ...options,
  });
}

// ---- Card (Braintree) ----

/**
 * Braintree client token for hosted-fields card entry. The token fetch is
 * wired; rendering the card form itself needs the `braintree-web` SDK, which
 * is not yet a dependency of this app (see PaymentDialog's Card tab).
 */
export function usePaymentClientToken(enabled = false) {
  return useQuery<string | null>({
    queryKey: ['billing', 'payment-client-token'],
    enabled,
    queryFn: async () => {
      const res = await dataCentralApi.post('/partner/payments/grant', {});
      const r = result<{ token?: string } | string>(res.data);
      return (typeof r === 'string' ? r : r?.token) ?? null;
    },
    staleTime: 5 * 60_000,
  });
}

export interface CreatePaymentMethodRequest {
  nonce: string;
  provider?: string;
  makeDefault?: boolean;
}

/** Persist a tokenized card (Braintree nonce) as a saved payment method. */
export function useCreatePaymentMethod(
  options?: UseMutationOptions<unknown, unknown, CreatePaymentMethodRequest>,
) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (req: CreatePaymentMethodRequest) => {
      const res = await dataCentralApi.post('/partner/payments/methods', {
        nonce: req.nonce,
        provider: req.provider || 'CreditCard',
        gateway: 'braintree',
        makeDefault: req.makeDefault ?? false,
      });
      return result(res.data);
    },
    ...options,
    onSuccess: (...args) => {
      qc.invalidateQueries({ queryKey: billingKeys.methods });
      options?.onSuccess?.(...args);
    },
  });
}

export interface ChargeSavedMethodRequest {
  methodId: string;
  invoiceId: string;
  amountVnd: number;
  amountUsd: number;
  description?: string;
}

/** Charge a saved payment method for an invoice (real money). */
export function useChargeSavedMethod(
  options?: UseMutationOptions<{ status?: string }, unknown, ChargeSavedMethodRequest>,
) {
  return useMutation({
    mutationFn: async (req: ChargeSavedMethodRequest) => {
      const res = await dataCentralApi.post(
        `/partner/payments/methods/${req.methodId}/transactions`,
        {
          invoiceId: req.invoiceId,
          amount: req.amountUsd,
          amountVnd: req.amountVnd,
          currency: 'USD',
          description: req.description || '',
        },
      );
      return result<{ status?: string }>(res.data) ?? {};
    },
    ...options,
  });
}

// ---- Top-up (deposit) ----

/** The deposit invoice created by a self-serve top-up, ready to pay. */
export interface DepositResult {
  invoiceId: string;
  amount: number;
  customerId: string;
  createdAt?: string;
}

interface DepositApiResult {
  invoice_id: number | string;
  amount: number;
  customer_id: number | string;
  created_at?: string;
}

/**
 * Create a self-serve top-up: a deposit invoice for `amount` VND. The returned
 * invoice is then paid via the normal PaymentDialog (PayPal/bank/MoMo); paying it
 * credits the wallet. `amount` is VND (the wallet currency).
 */
export function useCreateDeposit(
  options?: UseMutationOptions<DepositResult, unknown, { amount: number }>,
) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (req: { amount: number }) => {
      const res = await dataCentralApi.post('/partner/credits/deposit', {
        amount: req.amount,
      });
      const d = result<DepositApiResult>(res.data);
      return {
        invoiceId: String(d.invoice_id),
        amount: d.amount,
        customerId: String(d.customer_id),
        createdAt: d.created_at,
      } satisfies DepositResult;
    },
    onSuccess: (...args) => {
      // A new pending invoice now exists; refresh the invoice list.
      qc.invalidateQueries({ queryKey: ['billing', 'invoices'] });
      options?.onSuccess?.(...args);
    },
    ...options,
  });
}
